import { type FC, type ReactNode, useCallback, useMemo } from "react";
import CustomNumberFormat, { type NumberFormatEvent } from "~/common/components/CustomNumberFormat";
import { type LinearCstic } from "~/features/configurator/Linear/models";
import {
  CSTIC_PART_MAX_SIZE_NAME,
  CSTIC_PART_MIN_SIZE_NAME,
  GET_GUIDEWAY_PIECE_SIZE_CSTIC_NAME,
  GET_GUIDEWAY_PIECE_SIZE_SUGGESTION_HIGH_CSTIC_NAME,
  GET_GUIDEWAY_PIECE_SIZE_SUGGESTION_LOW_CSTIC_NAME,
} from "~/features/configurator/Linear/constants";
import {
  flushPendingInputChanges,
  refreshGuidewayPieceLen,
  updateGuidewayPieceLen,
  updateSubstepAdditionalData,
} from "~/features/configurator/Linear/redux/actions";
import { useDispatch, useSelector } from "react-redux";
import { getCsticNumberVal } from "~/features/configurator/Linear/utils";
import { NumberFormatter as NF } from "~/common/utils/NumberFormatter";
import classNames from "classnames";
import { getSubstepStep, selectRailKmatType } from "~/features/configurator/Linear/redux/selectors";
import { type SubstepType } from "~/features/configurator/Linear/dtos";
import { Card } from "~/features/configurator/Linear/components/Card";
import { useTranslation, type WithTranslation } from "react-i18next";
import { getGuidewayPieceDirtyParamName } from "~/features/configurator/common/railLengthUtils";
import debounce from "lodash/debounce";
import { styled } from "~/common/mui";

import { isNonNull } from "~/common/utils/reducers";

export const PieceLenContainer = styled("div")(({ theme }) => ({
  position: "relative",
  marginTop: theme.spacing(2.5),
}));

export const SuggestionList = styled(Card)({
  position: "absolute",
  bottom: "15px",
  transform: "translateY(100%)",
  left: 0,
  right: 0,
  background: "white",
  zIndex: 2,
  display: "flex",
  flexDirection: "column",
});

export const SuggestionItem = styled("div")(({ theme }) => ({
  padding: "10px 20px",
  cursor: "pointer",
  ...theme.typography.bodyLarge,
  "&:hover": {
    background: "#ebeef0",
  },
}));

interface Props {
  cstics: Dict<LinearCstic>;
  pieceIndex: number;
  kmatIndex: number;
  disabled?: boolean;
  isSecondLast?: boolean;
  isLast?: boolean;
  substep: SubstepType;
  makeMinMaxLabelFormatter: (t: WithTranslation["t"], min: number | null, max: number | null) => ReactNode;
}

export const PieceLengthInput: FC<Props> = ({
  cstics,
  pieceIndex,
  kmatIndex,
  disabled,
  substep,
  makeMinMaxLabelFormatter,
  isLast,
}) => {
  const dispatch = useDispatch();

  const kmatType = useSelector(selectRailKmatType);

  const length = getPieceLength(cstics, pieceIndex);
  const substepObj = useSelector(getSubstepStep(substep))!;
  const isDirty =
    substepObj.additionalData[getGuidewayPieceDirtyParamName(kmatType, kmatIndex, pieceIndex + 1)] ?? false;
  const setDirty = useCallback(
    debounce((isDirty: boolean) => {
      dispatch(
        updateSubstepAdditionalData(
          [
            {
              type: kmatType,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              value: isDirty as any,
              index: kmatIndex,
              key: GET_GUIDEWAY_PIECE_SIZE_CSTIC_NAME(pieceIndex + 1),
            },
          ].filter(isNonNull),
          substep,
        ),
      );
      dispatch(refreshGuidewayPieceLen(kmatType, kmatIndex, pieceIndex, substep, true));
    }, 500),
    [],
  );

  const updateVal = useCallback(
    (e: NumberFormatEvent) => {
      dispatch(updateGuidewayPieceLen(kmatType, kmatIndex, pieceIndex, substep, e.floatValue));
      setDirty(true);
    },
    [pieceIndex, kmatIndex, dispatch],
  );

  const { t } = useTranslation();

  let canShowSuggestion;
  if (pieceIndex === 0) {
    canShowSuggestion = true;
  } else if (isLast) {
    canShowSuggestion = false;
  } else {
    canShowSuggestion = new Array(pieceIndex).fill(1).every((_, index) => {
      const suggestionLowCstic = cstics[GET_GUIDEWAY_PIECE_SIZE_SUGGESTION_LOW_CSTIC_NAME(index + 1)];
      const suggestionLow = getCsticNumberVal(suggestionLowCstic);

      const suggestionHighCstic = cstics[GET_GUIDEWAY_PIECE_SIZE_SUGGESTION_HIGH_CSTIC_NAME(index + 1)];
      const suggestionHigh = getCsticNumberVal(suggestionHighCstic);
      return !suggestionLow && !suggestionHigh;
    });
  }

  const minLength = getCsticNumberVal(cstics[CSTIC_PART_MIN_SIZE_NAME]);
  const maxLength = getCsticNumberVal(cstics[CSTIC_PART_MAX_SIZE_NAME]);

  const suggestionLowCstic = cstics[GET_GUIDEWAY_PIECE_SIZE_SUGGESTION_LOW_CSTIC_NAME(pieceIndex + 1)];
  const suggestionLow = getCsticNumberVal(suggestionLowCstic);

  const suggestionHighCstic = cstics[GET_GUIDEWAY_PIECE_SIZE_SUGGESTION_HIGH_CSTIC_NAME(pieceIndex + 1)];
  const suggestionHigh = getCsticNumberVal(suggestionHighCstic);

  const pieceHelpLabel = makeMinMaxLabelFormatter(t, minLength, maxLength);

  const hasAtLeastOneSuggestion = [suggestionLow, suggestionHigh].filter(Boolean).length > 0;

  // TODO: This validation should be in state, so we can access it in next step
  let isInvalid = false;
  if (hasAtLeastOneSuggestion) {
    isInvalid = true;
  } else if (length !== null) {
    if (isDirty || disabled) {
      isInvalid = !!((minLength && length < minLength) || (maxLength && length > maxLength));
    }
  } else if (!length && isDirty) {
    isInvalid = true;
  }

  const { setLow, setHigh } = useMemo(
    () => ({
      setLow: () => {
        dispatch(updateGuidewayPieceLen(kmatType, kmatIndex, pieceIndex, substep, suggestionLow || undefined, true));
        setDirty(true);
        setDirty.flush();
      },
      setHigh: () => {
        dispatch(updateGuidewayPieceLen(kmatType, kmatIndex, pieceIndex, substep, suggestionHigh || undefined, true));
        setDirty(true);
        setDirty.flush();
      },
    }),
    [dispatch, suggestionLow, suggestionHigh],
  );

  return (
    <PieceLenContainer>
      <CustomNumberFormat
        value={length}
        handleState={updateVal}
        label={t("page.linear.guidewaySet.pieceLength.title", {
          index: pieceIndex + 1,
        })}
        error={isInvalid || false}
        helperText={<span>{pieceHelpLabel}</span>}
        decimalScale={2}
        disabled={disabled}
        onBlur={() => {
          flushPendingInputChanges();
          setDirty(true);
          setDirty.flush();
        }}
      />
      {canShowSuggestion && hasAtLeastOneSuggestion && (
        <SuggestionList variant={"square"} disableHover={true}>
          <p className={"pt-2 pb-1 px-3 f-label-color"}>{t("page.linear.guidewaySet.pieceLength.gridError")}</p>
          {suggestionLow && (
            <SuggestionItem onClick={setLow}>{NF.getDefaultFormatter().format(suggestionLow)}</SuggestionItem>
          )}
          {suggestionHigh && (
            <SuggestionItem onClick={setHigh}>{NF.getDefaultFormatter().format(suggestionHigh)}</SuggestionItem>
          )}
        </SuggestionList>
      )}
    </PieceLenContainer>
  );
};

const getPieceLength = (cstics: Dict<LinearCstic>, index: number) => {
  const csticName = GET_GUIDEWAY_PIECE_SIZE_CSTIC_NAME(index + 1);
  const cstic = cstics[csticName];
  if (cstic && cstic.value !== undefined) {
    return +cstic.value;
  }
  return null;
};
